import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    HelpCircle,
    X,
    Monitor,
    Mic,
    Volume2,
    Chrome,
    Power,
    KeyRound,
    Radio,
    ListChecks,
    Terminal,
    Package,
    Anchor,
    Building2,
    Rocket,
    ArrowLeftRight,
    ShieldAlert,
} from "lucide-react";

/**
 * HelpMenu — floating "?" button, bottom-left.
 * Opens a full-screen tactical reference with two tabs:
 *   1. SYSTEM SETUP   — recommended browser / OS / display / audio configuration
 *   2. VOICE COMMANDS — full spoken-command catalogue
 *
 * Designed to be TV-readable from across the room.
 */

const SETUP_SECTIONS = [
    {
        icon: Chrome,
        title: "Browser",
        lines: [
            "Google Chrome v120+ or Chromium Edge — required for Web Speech wake-word.",
            "Firefox and Safari are partially supported: click the mic orb manually.",
        ],
    },
    {
        icon: Mic,
        title: "Microphone Permission",
        lines: [
            "Click the lock icon in the address bar → Site settings → Microphone → Allow.",
            'Chrome flag for kiosks: launch with --use-fake-ui-for-media-stream to auto-grant.',
            "Physical mic must be plugged in BEFORE launching Chrome.",
        ],
    },
    {
        icon: Volume2,
        title: "Speaker Routing",
        lines: [
            "Open the voice orb → click the gear icon → pick input mic + output speaker.",
            "For TV audio, prefer HDMI-ARC or optical output to a wall speaker.",
            "Test-tone buttons live inside the audio settings modal.",
        ],
    },
    {
        icon: Monitor,
        title: "Display · Kiosk Mode",
        lines: [
            "Launch Chrome with:  --kiosk --autoplay-policy=no-user-gesture-required <URL>",
            "TV: set picture mode to Game / PC for lowest input lag. 1080p or 4K at 60Hz.",
            "Screen orientation should be landscape; app is a single-viewport layout.",
        ],
    },
    {
        icon: Power,
        title: "Power · Sleep",
        lines: [
            "OS: disable sleep, screensaver, and display-off timers.",
            "TV: disable Auto-off / Eco-mode / Ambient-light dimming.",
            "Chrome flag:  --disable-features=CalculateNativeWinOcclusion  (keeps tab active).",
        ],
    },
    {
        icon: KeyRound,
        title: "Login · Session",
        lines: [
            "First login: email code sent to @virtusmaritime.com only.",
            "Set a 4–6 digit PIN after first login for fast daily unlocks.",
            "Session travels in an httpOnly cookie; PIN is server-side hashed (bcrypt).",
        ],
    },
    {
        icon: ShieldAlert,
        title: "Network · Reliability",
        lines: [
            "Wired ethernet strongly recommended over Wi-Fi.",
            "Whitelist: emergentagent.com, api.openai.com.",
            "OpenAI TTS / STT and Whisper require outbound 443. Verify firewall.",
        ],
    },
];

const COMMAND_GROUPS = [
    {
        icon: Radio,
        code: "WAKE",
        title: "Wake Word",
        items: [
            { say: '"Hey Virtus"', do: "Arms the mic. Speak your command within 5 seconds." },
            { say: "Tap the orb", do: "Skips wake word · begins recording immediately." },
            { say: '"Close" · "Dismiss" · "Exit"', do: "Cancels any expanded overlay." },
        ],
    },
    {
        icon: ArrowLeftRight,
        code: "NAV",
        title: "Navigation",
        items: [
            { say: '"Show dev plan"', do: "Opens Development Plan." },
            { say: '"Show operations"', do: "Opens Operations." },
            { say: '"Show production"', do: "Opens Production." },
            { say: '"Show logistics"', do: "Opens Logistics." },
            { say: '"Show fleet"', do: "Opens Fleet." },
            { say: '"Show trials"', do: "Opens Trials." },
            { say: '"Show BD pipeline"', do: "Opens BD Pipeline (bd clearance)." },
            { say: '"Show screen saver"', do: "Opens the visitor showcase." },
            { say: '"Return to hub" · "Home"', do: "Back to the main tile grid." },
        ],
    },
    {
        icon: ListChecks,
        code: "BOARDS",
        title: "Dev Plan · Production",
        items: [
            { say: '"Expand task N"', do: "Full-screen the Nth row on the active board." },
            { say: '"Close task"', do: "Dismiss the task overlay." },
        ],
    },
    {
        icon: Package,
        code: "LOG",
        title: "Logistics",
        items: [
            { say: '"Expand order N"', do: "Detail view for order N." },
            { say: '"Mark order N delivered"', do: "Flip status to delivered." },
            { say: '"Add order"', do: "Opens the new-order form." },
        ],
    },
    {
        icon: Anchor,
        code: "FLEET",
        title: "Fleet",
        items: [
            { say: '"Expand vessel N"', do: "Full detail card for vessel N." },
            {
                say: '"Mark vessel N as <state>"',
                do: "States: on-station · transiting · deployed · in-maintenance · offline · lost-comms.",
            },
            { say: '"Add vessel"', do: "Opens the new-vessel form." },
        ],
    },
    {
        icon: Rocket,
        code: "TRIALS",
        title: "Trials",
        items: [
            { say: '"Expand trial N"', do: "Full detail card for trial N." },
            { say: '"Add trial" · "Log trial"', do: "Opens the new-trial form." },
        ],
    },
    {
        icon: Building2,
        code: "BD",
        title: "BD Pipeline",
        items: [
            { say: '"Expand deal N"', do: "Full detail card for deal N." },
            { say: '"Advance deal N"', do: "Bumps stage forward (lead → qualified → …)." },
            { say: '"Close deal N as won"', do: "Marks deal closed-won." },
            { say: '"Close deal N as lost"', do: "Marks deal closed-lost." },
            { say: '"Add deal"', do: "Opens the new-deal form." },
        ],
    },
    {
        icon: Terminal,
        code: "BRIEF",
        title: "Morning Brief",
        items: [
            {
                say: '"Run the brief" · "Give me the brief"',
                do: "Live 3-minute tactical brief with real board data.",
            },
            { say: "Auto", do: "10:00 local time fires the brief once per day." },
        ],
    },
];

export default function HelpMenu() {
    const [open, setOpen] = useState(false);
    const [tab, setTab] = useState("setup");

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="fixed bottom-6 left-6 z-[70] group flex items-center h-12 border border-virtus-line bg-virtus-surface/95 backdrop-blur hover:border-virtus-orange overflow-hidden transition-[max-width,border-color] duration-200 max-w-[48px] hover:max-w-[220px] pl-[13px] pr-3"
                data-testid="help-menu-open-btn"
                aria-label="Help and setup"
                title="System setup and voice commands"
            >
                <HelpCircle
                    size={22}
                    className="flex-shrink-0 text-virtus-dim group-hover:text-virtus-orange transition-colors"
                />
                <div className="ml-0 group-hover:ml-3 whitespace-nowrap overflow-hidden transition-[margin,opacity] duration-200 opacity-0 group-hover:opacity-100">
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint leading-none">
                        HELP
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-virtus-orange leading-none mt-1.5">
                        SETUP · CMDS
                    </div>
                </div>
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="fixed inset-0 z-[95] flex items-center justify-center bg-black/85 backdrop-blur-md p-6"
                        data-testid="help-menu-overlay"
                        onClick={() => setOpen(false)}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 12, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 12, scale: 0.98 }}
                            transition={{ duration: 0.18 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-[1400px] h-full max-h-[92vh] bg-[#0A0A0A] border-2 border-virtus-orange flex flex-col"
                        >
                            {/* Header */}
                            <div className="flex items-center justify-between px-6 py-4 border-b border-virtus-line">
                                <div className="flex items-center gap-4">
                                    <div className="w-8 h-8 border-2 border-virtus-orange flex items-center justify-center">
                                        <HelpCircle size={16} className="text-virtus-orange" />
                                    </div>
                                    <div>
                                        <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-virtus-orange">
                                            VIRTUS AI // FIELD MANUAL
                                        </div>
                                        <div className="font-heading font-black text-2xl uppercase tracking-tighter text-white">
                                            System Setup & Voice Commands
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="w-10 h-10 flex items-center justify-center border border-virtus-line text-virtus-dim hover:text-virtus-orange hover:border-virtus-orange"
                                    data-testid="help-menu-close-btn"
                                    aria-label="Close help"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="flex border-b border-virtus-line">
                                <TabButton
                                    active={tab === "setup"}
                                    onClick={() => setTab("setup")}
                                    label="01 · System Setup"
                                    testId="help-tab-setup"
                                />
                                <TabButton
                                    active={tab === "commands"}
                                    onClick={() => setTab("commands")}
                                    label="02 · Voice Commands"
                                    testId="help-tab-commands"
                                />
                            </div>

                            {/* Body */}
                            <div className="flex-1 overflow-y-auto p-6">
                                {tab === "setup" ? <SetupPanel /> : <CommandsPanel />}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between px-6 py-3 border-t border-virtus-line">
                                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-faint">
                                    VIRTUS AI · CLASSIFIED : INTERNAL USE
                                </div>
                                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-virtus-dim">
                                    PRESS <span className="text-virtus-orange">ESC</span> OR CLICK OUTSIDE TO CLOSE
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function TabButton({ active, onClick, label, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`px-6 py-3 font-mono text-[11px] tracking-[0.28em] uppercase border-r border-virtus-line transition-colors ${
                active
                    ? "text-virtus-orange bg-virtus-bg border-b-2 border-b-virtus-orange"
                    : "text-virtus-dim hover:text-white bg-virtus-surface"
            }`}
        >
            {label}
        </button>
    );
}

function SetupPanel() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {SETUP_SECTIONS.map((s, i) => (
                <SetupCard key={s.title} index={i} section={s} />
            ))}
        </div>
    );
}

function SetupCard({ section, index }) {
    const Icon = section.icon;
    return (
        <div className="border border-virtus-line bg-virtus-surface p-4 hover:border-virtus-orange transition-colors">
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-virtus-line">
                <div className="w-9 h-9 flex items-center justify-center border border-virtus-orange">
                    <Icon size={16} className="text-virtus-orange" />
                </div>
                <div>
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                        REQ · {String(index + 1).padStart(2, "0")}
                    </div>
                    <div className="font-heading text-lg font-black uppercase tracking-tight text-white">
                        {section.title}
                    </div>
                </div>
            </div>
            <ul className="space-y-2">
                {section.lines.map((l) => (
                    <li
                        key={l}
                        className="flex gap-2 text-sm text-virtus-dim leading-relaxed"
                    >
                        <span className="text-virtus-orange font-mono mt-0.5">▸</span>
                        <span>{l}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function CommandsPanel() {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {COMMAND_GROUPS.map((g) => (
                <CommandCard key={g.code} group={g} />
            ))}
        </div>
    );
}

function CommandCard({ group }) {
    const Icon = group.icon;
    return (
        <div className="border border-virtus-line bg-virtus-surface p-4 hover:border-virtus-orange transition-colors">
            <div className="flex items-center gap-3 mb-3 pb-3 border-b border-virtus-line">
                <div className="w-9 h-9 flex items-center justify-center border border-virtus-orange">
                    <Icon size={16} className="text-virtus-orange" />
                </div>
                <div>
                    <div className="font-mono text-[9px] tracking-[0.28em] uppercase text-virtus-faint">
                        AO/{group.code}
                    </div>
                    <div className="font-heading text-lg font-black uppercase tracking-tight text-white">
                        {group.title}
                    </div>
                </div>
            </div>
            <div className="space-y-2">
                {group.items.map((it) => (
                    <div
                        key={it.say}
                        className="grid grid-cols-[minmax(180px,_260px)_1fr] gap-3 py-1.5 items-start border-b border-virtus-line/50 last:border-b-0"
                    >
                        <div className="font-mono text-[11px] tracking-[0.12em] text-virtus-orange">
                            {it.say}
                        </div>
                        <div className="text-sm text-virtus-dim leading-snug">
                            {it.do}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
